import { CalendarDays, X } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseDate, today as todayString } from '../../lib/date';
import { cn } from '../../lib/utils';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * Ô chọn ngày — thay cho `<input type="date">`.
 *
 * ## Vì sao thay
 *
 * `<input type="date">` trông **khác nhau ở mọi trình duyệt**: Chrome cho một
 * ô ba khối `dd/mm/yyyy` với mũi tên nhỏ xíu, Firefox cho một kiểu khác, Safari
 * trên máy tính gần như không cho gì. Không style được phần bên trong, không
 * đổi được ngôn ngữ (nó theo *ngôn ngữ hệ điều hành*, nên một người dùng máy
 * tiếng Anh vẫn thấy `mm/dd/yyyy` giữa giao diện tiếng Việt), và biểu tượng
 * lịch của trình duyệt không theo màu nào của ứng dụng.
 *
 * ## Vẫn gõ được, không chỉ bấm
 *
 * Nhập bằng bàn phím **nhanh hơn** chọn trên lịch khi người ta đã biết ngày —
 * đặc biệt với ngày xa hôm nay. Nên đây là một `<input>` thật: gõ thẳng
 * `YYYY-MM-DD`, và cái lịch chỉ là lối tắt. Bỏ ô nhập đi là bắt người nhập
 * ngày sinh năm 1985 bấm mũi tên bốn trăm lần.
 *
 * ## Giá trị vào/ra luôn là `YYYY-MM-DD`
 *
 * Cùng dạng với `<input type="date">` cũ và với `CalendarDate` của
 * `@apm/contracts`, nên thay thế tại call site không phải đụng vào logic nào.
 * Chuỗi rỗng nghĩa là "chưa chọn" và được trả về nguyên như thế.
 */
export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** Cho phép xoá về rỗng. Mặc định `true`. */
  clearable?: boolean;
  id?: string | undefined;
  className?: string | undefined;
  /**
   * Lớp cho khung lịch. Lịch là Popover gắn ở `body` với `z-50`; ô ngày nằm trong
   * một hộp thoại tự dựng ở lớp cao hơn (`z-[60]`, như popup thiếu hạn / ước lượng)
   * thì lịch mở ra **sau** hộp thoại và không bấm được — truyền `z-[70]`.
   */
  popoverClassName?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-describedby'?: string | undefined;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder,
  disabled,
  clearable = true,
  id,
  className,
  popoverClassName,
  ...aria
}: DatePickerProps): ReactNode {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  /** Chữ đang gõ dở. `null` = ô đang hiện giá trị đã chốt. */
  const [draft, setDraft] = useState<string | null>(null);

  const locale = i18n.language === 'en' ? 'en-GB' : 'vi-VN';
  const pretty = useMemo(() => {
    if (!isCompleteDate(value)) return '';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(parseDate(value));
  }, [locale, value]);

  const commit = (text: string): void => {
    setDraft(null);
    if (text === '') {
      onChange('');
      return;
    }

    // Gõ dở rồi rời ô thì **quay về giá trị cũ**, không đoán. Im lặng biến
    // `15/9` thành một ngày nào đó là cách chắc chắn nhất để lưu sai hạn chót
    // mà không ai biết.
    const parsed = parseTyped(text);
    if (parsed !== null) onChange(parsed);
  };

  return (
    <div className={cn('relative', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <input
          id={id}
          // `text`, không phải `date`: đây là lý do component này tồn tại.
          type="text"
          inputMode="numeric"
          disabled={disabled}
          // Gõ dở thì hiện đúng thứ đang gõ; nghỉ gõ thì hiện dạng đã chuẩn hoá.
          value={draft ?? pretty}
          placeholder={placeholder ?? t('calendar.placeholder')}
          // Bấm vào ô thì sửa **đúng thứ đang nhìn thấy**. Nhảy từ `15/09/2026`
          // sang `2026-09-15` ngay lúc đặt con trỏ là đổi chữ dưới tay người
          // dùng — họ tưởng mình vừa xoá mất gì đó.
          onFocus={() => setDraft(pretty)}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={(event) => commit(event.target.value.trim())}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(event.currentTarget.value.trim());
            } else if (event.key === 'Escape' && draft !== null) {
              setDraft(null);
            }
          }}
          className={cn(
            // Viền và bo góc như `Input` — ô ngày đứng cạnh ô chữ và `select` trong mọi form;
            // `border-input` nhạt hơn làm nó trông như không có viền (2026-10-05).
            'flex h-9 w-full rounded-control border border-border-strong bg-background py-1 pl-3 text-sm shadow-xs transition-colors',
            'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
            'aria-invalid:border-destructive',
            // Chừa chỗ cho một hoặc hai nút bên phải.
            clearable && value ? 'pr-16' : 'pr-9',
          )}
          {...aria}
        />

        <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
          {clearable && value !== '' && !disabled && (
            <button
              type="button"
              aria-label={t('calendar.clear')}
              onClick={() => {
                setDraft(null);
                onChange('');
              }}
              className="inline-flex size-7 items-center justify-center text-muted-foreground hover:text-foreground rounded-full glass-hover"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          )}

          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={t('calendar.open')}
              className="inline-flex size-7 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50 rounded-full glass-hover"
            >
              <CalendarDays className="size-4" aria-hidden />
            </button>
          </PopoverTrigger>
        </div>

        <PopoverContent className={cn('w-auto', popoverClassName)}>
          <Calendar
            value={value === '' ? undefined : value}
            min={min}
            max={max}
            onSelect={(next) => {
              setDraft(null);
              onChange(next);
              // Đóng ngay sau khi chọn: giữ lịch mở buộc người dùng phải tìm
              // chỗ bấm ra ngoài, và họ thường bấm nhầm vào một ngày khác.
              setOpen(false);
            }}
          />

          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                onChange(todayString());
                setOpen(false);
              }}
              className="rounded-DEFAULT px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-muted"
            >
              {t('calendar.today')}
            </button>

            {clearable && value !== '' && (
              <button
                type="button"
                onClick={() => {
                  setDraft(null);
                  onChange('');
                  setOpen(false);
                }}
                className="rounded-DEFAULT px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {t('calendar.clear')}
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * `YYYY-MM-DD` **và** là một ngày có thật.
 *
 * Kiểm cả hai vì regex một mình nhận `2026-02-31`: `new Date` sẽ cuộn nó sang
 * ngày 3 tháng 3, và người dùng nhận một hạn chót họ không hề gõ.
 */
export function isCompleteDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = parseDate(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * Chuỗi người dùng gõ → `YYYY-MM-DD`, hoặc `null` nếu không đọc được.
 *
 * Nhận **hai** dạng có chủ đích:
 *
 * - `15/09/2026` — thứ ô này đang hiển thị, nên là thứ người ta sửa rồi gõ lại.
 *   Cũng nhận `15-9-2026` và `15.9.2026`: dấu phân cách là thứ người dùng gõ
 *   theo thói quen bàn phím, không phải một quy tắc họ cần học.
 * - `2026-09-15` — dạng ISO. Giữ lại vì nó là dạng người dán vào từ chỗ khác
 *   (URL, bảng tính, một tab khác của chính ứng dụng này).
 *
 * ⚠️ Không dùng `new Date(text)`: nó đọc `01/02/2026` là **tháng Giêng ngày 2**
 * theo lối Mỹ, ngược hẳn với thứ người dùng Việt Nam vừa gõ. Đây là loại lỗi
 * chỉ lộ ra với những ngày ≤ 12, tức là không bao giờ lộ trong lúc thử nhanh.
 */
export function parseTyped(text: string): string | null {
  const iso = text.trim();
  if (isCompleteDate(iso)) return iso;

  const match = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(iso);
  if (!match) return null;

  const [, day, month, year] = match;
  const candidate = `${year!}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`;
  return isCompleteDate(candidate) ? candidate : null;
}
